/**
 * gd-collection-grid.js — motor CLIENT-SIDE de la grilla de colección (Galpón).
 *
 * Dos modos de vista, ambos client-side (cero fetch), conmutados in-situ:
 *
 *  VISTA A — COLECCIÓN (default). La grilla colapsa a 1 card por FAMILIA
 *    (data-family = synaboard.product_id). El representante (primero del DOM) queda
 *    visible; los hermanos se ocultan. La card de una familia N>1 es una ENTRADA de
 *    categoría (foto del rep + "N variantes · desde $X"), NO un producto comprable →
 *    al click NO va a la PDP, sino que ENTRA a la familia (vista B). Las familias de
 *    1 (standalone) son cards de producto normales → click va a su PDP.
 *    Los pills de subcategoría filtran los representantes en memoria.
 *
 *  VISTA B — DENTRO DE UNA FAMILIA (drill-down). La grilla muestra SOLO los hermanos
 *    de la familia elegida (cada uno su card de producto → click va a su PDP). Un
 *    breadcrumb "‹ Cocina › Cojines" permite volver. Los pills se ocultan (no tiene
 *    sentido filtrar dentro de una familia). Todo client-side, instantáneo, sin
 *    recarga ni fetch.
 *
 * Por qué client-side y drill-down (no acordeón): el cliente es mayormente MOBILE
 * (pauta/IG); en touch no hay hover → el prefetch nativo de Horizon es inútil. Con
 * toda la hoja en el DOM (main-collection.liquid pagina by 250 en colección), entrar/
 * filtrar = mostrar/ocultar nodos. El drill-down (vs. acordeón) evita la ambigüedad
 * "¿la card de familia es un producto o un grupo?": adentro cada foto = un producto,
 * la entrada = un grupo. Ver decisions 2026-06-23.
 *
 * Progressive enhancement: sin JS, las cards son <a> a la PDP del rep y la grilla
 * muestra todo sin colapsar — degradado pero funcional.
 */
(() => {
  const GRID_SELECTOR = '.product-grid';
  const ITEM_SELECTOR = '.product-grid__item';
  const PILL_SELECTOR = '.gd-subcat';
  const HIDDEN_FILTER = 'gd-hidden-by-filter'; // rep oculto por el pill activo
  const HIDDEN_SIBLING = 'gd-hidden-sibling'; // hermano colapsado (vista A)
  const HIDDEN_DRILL = 'gd-hidden-by-drill'; // oculto porque estamos dentro de OTRA familia

  // Memoria del pill activo POR colección, para que "volver" a la colección desde una PDP
  // restaure el mismo filtro que tenías → la altura del grid coincide con la que dejaste
  // → el scroll-restore (gd-return-position.js) cae en la card correcta. Sin esto, volver
  // re-renderiza en "Todo" (más alto) y el scroll guardado apunta a otro producto. Clave
  // por pathname → Muebles y Objetos (futuro) no se pisan. sessionStorage (no URL): sin
  // flash al recargar, efímero por pestaña. Ver decisions 2026-06-29.
  const FILTER_KEY = 'gd:collection-filter';
  function filterStoreKey() { return FILTER_KEY + ':' + location.pathname; }
  function saveActiveType(type) {
    try {
      if (type) sessionStorage.setItem(filterStoreKey(), type);
      else sessionStorage.removeItem(filterStoreKey());
    } catch {}
  }
  function loadActiveType() {
    try { return sessionStorage.getItem(filterStoreKey()) || ''; } catch { return ''; }
  }

  // Set/borra un query param SIN recargar, preservando los demás params y el hash.
  // value=null → borra el param. Lo usa el deep-link de familia (?familia=) para que la vista
  // B sea compartible/recargable.
  //   mode 'push'    → history.pushState: CREA una entrada nueva → "atrás" del navegador la
  //                    deshace (entrar a familia = un paso navegable). Es el default al DRILL.
  //   mode 'replace' → history.replaceState: pisa la entrada actual (sin crecer el historial).
  //                    Se usa cuando NO queremos un nuevo paso (ej. sincronizar la URL sin
  //                    alterar el historial).
  function setURLParam(key, value, mode) {
    try {
      const url = new URL(location.href);
      if (value == null) url.searchParams.delete(key);
      else url.searchParams.set(key, value);
      const target = url.pathname + url.search + url.hash;
      if (mode === 'push') history.pushState({ gdFamily: value || null }, '', target);
      else history.replaceState({ gdFamily: value || null }, '', target);
    } catch {}
  }

  /** Formatea la unidad menor (Shopify da centavos) a "$12.345" (CLP, sin decimales). */
  function formatPrice(cents) {
    const pesos = Math.round(Number(cents) / 100);
    return '$' + pesos.toLocaleString('es-CL');
  }

  // ── HIDRATACIÓN (D-coleccion-1, jul 2026) ──────────────────────────────────
  // La página inicial trae ~16 cards COMPLETAS + el resto como <li> livianos
  // (.gd-card-pending: solo data-attrs — suficientes para colapso/pills/conteos/orden
  // desde el primer frame — y un <a> de texto para no-JS). Acá se traen las cards
  // reales en UN fetch (?view=gd-hydrate, layout mínimo, gzip ~10:1) y se swapean en
  // CHUNKS por frame (no un innerHTML gigante que trabe el hilo). Al terminar:
  // re-colapso (los reps que eran pending recuperan su .gd-family-meta, que vive en el
  // contenido swapeado) + señal 'gd:grid-hydrated' + clase en <html> para los gates
  // (drill, deep-link, scroll-restore profundo).
  // DEGRADACIÓN: si el fetch falla, los pending quedan ocultos pero el grid visible
  // sigue 100% funcional, y la señal se emite IGUAL (ningún gate puede colgarse
  // esperando una hidratación que no va a llegar — regla dura del theme).
  let hydratePromise = null;
  function markHydrated() {
    document.documentElement.classList.add('gd-grid-hydrated');
    document.dispatchEvent(new CustomEvent('gd:grid-hydrated'));
  }
  function hydrate(grid) {
    if (hydratePromise) return hydratePromise;
    if (!grid.querySelector('.gd-card-pending')) {
      hydratePromise = Promise.resolve();
      markHydrated();
      return hydratePromise;
    }
    const url = location.pathname + '?view=gd-hydrate';
    hydratePromise = fetch(url, { credentials: 'same-origin' })
      .then((r) => {
        if (!r.ok) throw new Error('hydrate ' + r.status);
        return r.text();
      })
      .then((html) => new Promise((resolve) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const map = new Map();
        doc.querySelectorAll(ITEM_SELECTOR).forEach((li) => {
          map.set(li.getAttribute('data-product-id'), li);
        });
        const pending = [...grid.querySelectorAll('.gd-card-pending')];
        const CHUNK = 12; // cards por frame: mantiene los frames < ~8ms en móvil
        (function swap(i) {
          const end = Math.min(i + CHUNK, pending.length);
          for (; i < end; i++) {
            const li = pending[i];
            const src = map.get(li.getAttribute('data-product-id'));
            if (src) li.innerHTML = src.innerHTML;
            li.classList.remove('gd-card-pending');
          }
          if (i < pending.length) requestAnimationFrame(() => swap(i));
          else resolve();
        })(0);
      }))
      .then(() => {
        // Integración QUIRÚRGICA (no re-colapso): el colapso/orden inicial ya corrió con
        // el índice COMPLETO (los data-attrs viajan también en los <li> livianos) → el
        // orden del DOM ya es el final y las clases de colapso viven en los <li> (el swap
        // de innerHTML no las toca). Re-correr collapseFamilies+orderSoldOutLast acá
        // movía 125 nodos (appendChild masivo) hacia posiciones donde YA estaban →
        // reflow visible = "parpadeo al llegar" (cazado por F). Lo ÚNICO que el swap
        // borra es el .gd-family-meta de los reps que eran pending (vivía en su
        // contenido) → re-inyectarlo, y re-marcar columnas (solo clases, sin mover nodos).
        for (const [, group] of familyMap) {
          if (group.members.length > 1) {
            if (!group.rep.querySelector('.gd-family-meta')) {
              decorateRepresentative(group.rep, group.members);
            }
            // refresh de mini-thumbs: los hermanos que eran pending recién ahora tienen
            // <img>. buildFamilyThumbs es idempotente (solo reconstruye si ganó fotos).
            buildFamilyThumbs(group.rep, group.members);
          }
        }
        markGridColumns(grid);
        markHydrated();
      })
      .catch(() => { markHydrated(); });
    return hydratePromise;
  }
  // Gate compartido: entra a una familia AHORA si sus cards están completas, o tras la
  // hidratación si tiene pendientes (tapando la espera con el patrón gd-grid-restoring,
  // que tiene su propio failsafe CSS — nunca puede dejar el grid oculto para siempre).
  function enterFamilyWhenReady(grid, family, auto, fromHistory) {
    const group = familyMap.get(family);
    if (!group) return;
    const hasPending = group.members.some((m) => m.classList.contains('gd-card-pending'));
    if (!hasPending) {
      enterFamily(grid, family, auto, fromHistory);
      return;
    }
    grid.classList.add('gd-grid-restoring');
    const failsafe = setTimeout(() => grid.classList.remove('gd-grid-restoring'), 4000);
    hydrate(grid).then(() => {
      clearTimeout(failsafe);
      grid.classList.remove('gd-grid-restoring');
      enterFamily(grid, family, auto, fromHistory);
    });
  }
  // Expuesta para gd-return-position (scroll profundo necesita la altura final).
  window.gdGridHydration = () => hydratePromise || Promise.resolve();

  // Mapa family → { rep, members[] }. members[0] === rep (primero del DOM).
  const familyMap = new Map();
  // Estado de vista: '' = vista A (colección); '<family>' = vista B (dentro de esa familia).
  let drilledFamily = '';
  // scrollY de la vista A al momento de entrar a una familia → para restaurarlo al
  // volver (reaparecés justo en la card que clickeaste, no perdido).
  let scrollBeforeDrill = 0;
  // Orden CURADO del catálogo: el orden de los <li> tal como los entregó el server,
  // capturado UNA vez (inmutable). Es la base de la opción "Características" y el
  // fallback. NO se toca al ordenar ni al entrar/salir de familias.
  let originalOrder = null;
  // Snapshot del orden del DOM justo antes de entrar a una familia (el orden curado).
  // Al salir restauramos ESTE → las cards vuelven a su posición exacta.
  let orderBeforeDrill = null;
  // Filtro de subcategoría EN MEMORIA (no en URL → sin flash al recargar; sin deep-link,
  // irrelevante para esta tienda). '' = "Todo". Ver decisions 2026-06-23.
  let activeType = '';

  /** ¿este <li> (producto) está DISPONIBLE? data-available lo pone Liquid (product.available). */
  function isAvailable(li) {
    return li.getAttribute('data-available') !== 'false';
  }

  /**
   * AGOTADOS AL FINAL, sin romper el orden de Shopify. Sort ESTABLE por una sola clave
   * (disponible sí/no): particiona [disponibles..., agotados...] preservando el orden
   * relativo original DENTRO de cada grupo. Como el orden de Shopify es MOST_RELEVANT
   * (relevancia), ese orden se conserva intacto entre los disponibles y entre los agotados
   * — solo se separan en dos bloques. NO reordena por otra cosa ni aleatoriza nada.
   * (Array.prototype.sort es estable en todos los motores modernos desde ES2019, pero para
   * no depender de eso hacemos la partición explícita, que es estable por construcción.)
   */
  function stableSortAvailableFirst(list) {
    const available = [];
    const soldOut = [];
    for (const li of list) {
      (isAvailable(li) ? available : soldOut).push(li);
    }
    return available.concat(soldOut);
  }

  /**
   * Agrupa los items por data-family, deja un representante visible por familia y
   * oculta el resto. Marca las familias N>1 como entrables (data-family-key) y les
   * inyecta el meta "N variantes · desde $X". Llena familyMap.
   */
  function collapseFamilies(grid) {
    const items = [...grid.querySelectorAll(ITEM_SELECTOR)];
    // capturar el orden original del DOM UNA sola vez (antes de cualquier reorder).
    if (originalOrder === null) originalOrder = items.slice();
    familyMap.clear();

    for (const li of items) {
      const family = li.getAttribute('data-family') || li.getAttribute('data-product-id');
      if (!familyMap.has(family)) familyMap.set(family, { rep: li, members: [] });
      familyMap.get(family).members.push(li);
    }

    for (const [family, group] of familyMap) {
      // AGOTADOS AL FINAL (dentro de la familia): reordenar los miembros con los DISPONIBLES
      // primero, preservando el orden relativo de Shopify dentro de cada grupo (sort estable
      // por una sola clave = ¿disponible?). Doble beneficio: (1) el REPRESENTANTE (members[0],
      // cuya foto es la de la card de familia) pasa a ser un acabado CON stock → la vitrina no
      // muestra la foto de algo agotado; (2) al entrar al drill, los agotados quedan al final.
      // Si TODOS están agotados, el orden no cambia (la familia entera está agotada). No rompe
      // el orden de Shopify: solo particiona disponibles/agotados sin reordenar dentro de cada uno.
      group.members = stableSortAvailableFirst(group.members);
      group.rep = group.members[0];
      const { members } = group;
      members.forEach((li, i) => {
        if (i === 0) {
          li.classList.remove(HIDDEN_SIBLING);
          li.classList.add('gd-family-rep');
          li.setAttribute('data-family-size', String(members.length));
          if (members.length > 1) {
            li.setAttribute('data-family-key', family);
            decorateRepresentative(li, members);
            buildFamilyThumbs(li, members);
            resolveFamilyBadge(li, members);
          }
        } else {
          li.classList.add(HIDDEN_SIBLING);
        }
      });
    }
    return familyMap;
  }

  /**
   * Badge "Agotado" en la card de FAMILIA: solo debe mostrarse si TODOS los miembros están
   * agotados. El badge lo pinta Liquid según `product.available` del REPRESENTANTE (primer
   * miembro del DOM) — que puede estar agotado aunque un hermano tenga stock → badge incorrecto.
   * Acá, en el colapso, recalculamos a nivel familia: si AL MENOS UN miembro tiene stock
   * (data-available="true"), ocultamos el badge "sold-out" del representante. Si todos están
   * agotados, lo dejamos. (El badge de OFERTA —compare_at_price— NO se toca: es válido siempre.)
   * En cards INDIVIDUALES (N=1, no pasa por acá) el badge de Liquid queda tal cual — correcto.
   */
  function resolveFamilyBadge(rep, members) {
    // Solo actúa si el REP está agotado (su badge, pintado por Liquid, es el "sold-out").
    // Si el rep tiene stock, no pintó badge sold-out → nada que ocultar.
    if (rep.getAttribute('data-available') !== 'false') return;
    // ¿algún OTRO miembro tiene stock? Si sí, la familia NO está agotada → el badge sold-out
    // del rep es engañoso → ocultarlo. Si TODOS agotados → dejarlo (la familia sí está agotada).
    const anyAvailable = members.some((li) => li.getAttribute('data-available') === 'true');
    if (!anyAvailable) return;
    // Marca en el <li>: el CSS oculta el badge sold-out sin borrar el de oferta. Como el Liquid
    // pinta UN solo badge de estado a la vez (if/elsif sold-out|sale) y sabemos que el rep está
    // agotado, el badge presente ES el sold-out → ocultar el contenedor de badges del rep.
    rep.classList.add('gd-family-has-stock');
  }

  /**
   * Inyecta el meta de familia en DOS líneas, para que la card de familia tenga la
   * misma estructura de 3 filas que la individual (precios alineados a la misma
   * altura en la grilla):
   *   fila 2: "N piezas"        (.gd-family-count) — equivale a las medidas de la individual
   *   fila 3: "desde $X"        (.gd-family-price) — equivale al precio de la individual
   * "piezas" (no "variantes") = vocabulario de marca, coherente con el conteo de la
   * colección. Sin "ver familia": el conteo + el hover ya comunican que es entrable.
   */
  function decorateRepresentative(rep, members) {
    if (rep.querySelector('.gd-family-meta')) return; // idempotente

    let min = Infinity;
    for (const li of members) {
      const p = Number(li.getAttribute('data-price'));
      if (!Number.isNaN(p) && p < min) min = p;
    }

    const wrap = document.createElement('span');
    wrap.className = 'gd-family-meta';

    const count = document.createElement('span');
    count.className = 'gd-family-count';
    count.textContent = members.length + ' piezas';
    wrap.appendChild(count);

    if (Number.isFinite(min)) {
      const price = document.createElement('span');
      price.className = 'gd-family-price';
      // "desde" en su propio span → más chico/tenue que el número (jerarquía, no color:
      // el terracota es para acentos/acciones, pintarlo ahí confundiría). El número
      // mantiene el peso del precio normal → uniformidad en la grilla.
      const word = document.createElement('span');
      word.className = 'gd-family-price__word';
      word.textContent = 'desde ';
      price.appendChild(word);
      price.appendChild(document.createTextNode(formatPrice(min)));
      wrap.appendChild(price);
    }

    const anchor =
      rep.querySelector('.product-card__content') ||
      rep.querySelector('.product-card__info') ||
      rep;
    anchor.appendChild(wrap);
  }

  /**
   * Fila de MINI-THUMBS de los hermanos en la card de familia (decisión 2-ago-2026,
   * elegida por F sobre screenshots reales contra chip "+N" y texto solo — el texto
   * "N piezas" YA existía y una grabación de Clarity mostró que no se ve).
   * UNA fila fija de hasta MAX slots con las fotos de los HERMANOS (el rep ya es la
   * foto grande de la card). Si hay más hermanos que slots, el ÚLTIMO slot es un
   * badge "+N" (regla de F). Cero requests nuevos de datos: las fotos salen de los
   * <li> hermanos ya presentes (colapsados) en el DOM; solo se re-piden al CDN en 96px.
   * Sin interacción propia (pointer-events:none): el click cae a la card y entra a la
   * familia, idéntico a pinchar la foto principal (confirmado por F).
   * Idempotente y re-hidratable: los hermanos "pending" (cards livianas sin <img>)
   * aún no aportan foto; al llegar la hidratación, collapseFamilies re-corre y esta
   * función REEMPLAZA la fila si ahora puede pintar más thumbs que antes.
   */
  function buildFamilyThumbs(rep, members) {
    const MAX = 3; // slots totales de la fila (thumbs o badge)
    const siblings = members.slice(1);
    if (!siblings.length) return;

    const srcs = [];
    for (const li of siblings) {
      // data-thumb viaja en TODOS los <li> desde el server (main-collection.liquid),
      // también en los livianos → la fila se pinta COMPLETA en el primer colapso, sin
      // esperar la hidratación (sin esto, los thumbs de hermanos pending aparecían
      // "de la nada" 1-2s tarde). El <img> de la card queda como fallback.
      const attr = li.getAttribute('data-thumb');
      if (attr) { srcs.push(attr); continue; }
      const img = li.querySelector('.card-gallery img, img');
      const src = img && (img.currentSrc || img.getAttribute('src'));
      if (src) srcs.push(src);
    }

    const useBadge = siblings.length > MAX;
    const visible = useBadge ? MAX - 1 : Math.min(siblings.length, MAX);
    const existing = rep.querySelector('.gd-family-thumbs');
    const paintable = Math.min(visible, srcs.length);
    // idempotencia: reconstruir SOLO si ganamos algo — thumbs nuevos, o la foto que
    // le faltaba al badge "+N" para pasar de plano a peek (bug cazado por F: el
    // fallback plano quedaba pegado aunque la hidratación ya trajera la foto).
    const wantPeek = useBadge && srcs.length > paintable;
    if (existing) {
      const prevThumbs = Number(existing.getAttribute('data-thumbs')) || 0;
      const prevPeek = !!existing.querySelector('.gd-family-thumbs__more--peek');
      if (prevThumbs >= paintable && prevPeek === wantPeek) return;
      existing.remove();
    }
    if (paintable === 0) return; // todos pending aún: la hidratación re-invoca.

    const row = document.createElement('div');
    row.className = 'gd-family-thumbs';
    row.setAttribute('data-thumbs', String(paintable));
    row.setAttribute('aria-hidden', 'true'); // decorativa: "N piezas" ya lo anuncia

    for (let i = 0; i < paintable; i++) {
      // <span> con background-image y NO <img>: Horizon fuerza width:100% sobre
      // cualquier img de la card y ganarle exigiría !important; el span queda
      // fuera de esas reglas. Mismo asset del hermano, re-pedido chico al CDN
      // (96px = 38px CSS a DPR 2). Si la URL no trae width, se deja tal cual.
      const im = document.createElement('span');
      im.className = 'gd-family-thumbs__img';
      im.style.backgroundImage = 'url("' + srcs[i].replace(/width=\d+/, 'width=96') + '")';
      row.appendChild(im);
    }
    if (useBadge) {
      const more = document.createElement('span');
      more.className = 'gd-family-thumbs__more';
      more.textContent = '+' + (siblings.length - paintable);
      // Si hay foto del siguiente hermano, el badge la muestra ASOMANDO tras un velo
      // oscuro (patrón "+N sobre la próxima foto") en vez de un cuadrado plano. El
      // gradiente y la url van en UN solo background → el texto queda encima sin capas.
      const next = srcs[paintable];
      if (next) {
        more.classList.add('gd-family-thumbs__more--peek');
        more.style.backgroundImage =
          'linear-gradient(rgba(39,37,34,.72), rgba(39,37,34,.72)), url("' +
          next.replace(/width=\d+/, 'width=96') + '")';
      }
      row.appendChild(more);
    }

    // OVERLAY dentro de la foto (esquina inferior izquierda), NUNCA como fila propia:
    // (a) entre foto y título desalineaba nombre/medida/precio con las cards vecinas
    // (la grilla está diseñada con esas filas a la misma altura — ver .gd-family-meta
    // en galpon-overrides.css); (b) al final de la card quedaban como migas flotando
    // bajo el precio (ambas descartadas por F mirando el dev server, 2-ago). Adentro
    // de la foto no agregan alto (alineación intacta) y quedan ancladas a lo que son.
    const gallery = rep.querySelector('.card-gallery');
    const anchor = gallery || rep.querySelector('.product-card__content') || rep;
    anchor.appendChild(row);
  }

  /** Nombre legible (product_name) de una card, leído de su heading. */
  function repName(li) {
    const titleEl =
      li.querySelector('.gd-product-name') ||
      li.querySelector('[role="heading"]') ||
      li.querySelector('.product-card__title');
    return titleEl ? titleEl.textContent.trim() : '';
  }

  /** Nombre legible de una familia (para el breadcrumb): el product_name del rep. */
  function familyName(group) {
    return repName(group.rep);
  }

  // ---- VISTA: filtro de pills (solo aplica en vista A) -----------------------

  function applyFilter(grid) {
    if (drilledFamily) return; // en vista B no filtramos por pills
    const reps = grid.querySelectorAll('.gd-family-rep');
    const hasFilter = activeType !== '';
    let productos = 0; // PRODUCTOS reales (no cards): una familia suma sus N
    reps.forEach((rep) => {
      const type = rep.getAttribute('data-type') || '';
      const match = !hasFilter || type === activeType;
      rep.classList.toggle(HIDDEN_FILTER, !match);
      if (match) productos += Number(rep.getAttribute('data-family-size')) || 1;
    });
    // sin filtro: usar el total REAL de Shopify (data-total), que es la verdad
    // independiente de render/colapso. Con filtro: el subconjunto sumado arriba.
    if (!hasFilter) {
      const el = document.querySelector('.gd-collection-count');
      const total = el && Number(el.getAttribute('data-total'));
      if (total) productos = total;
    }
    updateCount(productos);
  }

  function updateCount(n) {
    const el = document.querySelector('.gd-collection-count');
    if (!el) return;
    el.textContent = n === 1 ? '1 pieza' : n + ' piezas';
  }

  function syncPills() {
    const hasFilter = activeType !== '';
    document.querySelectorAll('.gd-subcats').forEach((nav) => {
      nav.querySelectorAll(PILL_SELECTOR).forEach((pill) => {
        const type = pill.getAttribute('data-type') || '';
        const isActive = type === '' ? !hasFilter : type === activeType;
        pill.classList.toggle('gd-subcat--active', isActive);
        if (isActive) pill.setAttribute('aria-current', 'true');
        else pill.removeAttribute('aria-current');
      });
    });
  }

  // ---- DRILL-DOWN: entrar / salir de una familia -----------------------------

  /**
   * Entra a una familia (vista B): muestra SOLO sus hermanos (reordenados consecutivos),
   * oculta todo lo demás del grid, oculta pills, pinta breadcrumb. Cero fetch.
   */
  /**
   * Animación de entrada de las cards VISIBLES tras un cambio de vista (entrar/salir de
   * familia). Coherente con el lenguaje de movimiento del sitio (velo, navbar): fade-in +
   * leve subida (8px desde abajo), curva de marca. Stagger sutil capeado a los primeros
   * items para no alargar familias grandes. CSS-driven: agregamos .gd-card-enter (estado
   * inicial: opacity 0 + translateY) y la quitamos en el frame siguiente → transiciona al
   * estado natural. Respeta prefers-reduced-motion (el CSS anula la transición).
   */
  function animateGridEntrance(grid) {
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const visible = [...grid.querySelectorAll(ITEM_SELECTOR)].filter(
      (li) => !li.classList.contains(HIDDEN_DRILL)
        && !li.classList.contains(HIDDEN_SIBLING)
        && !li.classList.contains(HIDDEN_FILTER)
    );
    visible.forEach((li, i) => {
      // stagger sutil: 40ms por card, capeado a 8 → familias grandes no se alargan.
      li.style.setProperty('--gd-card-enter-delay', Math.min(i, 8) * 40 + 'ms');
      li.classList.add('gd-card-enter');
    });
    // doble rAF: el browser registra el estado inicial (opacity 0) antes de quitar la clase.
    requestAnimationFrame(() => requestAnimationFrame(() => {
      visible.forEach((li) => {
        li.classList.remove('gd-card-enter');
        // limpiar la var tras la transición para no dejar estado colgado.
        li.addEventListener('transitionend', function te() {
          li.style.removeProperty('--gd-card-enter-delay');
          li.removeEventListener('transitionend', te);
        }, { once: true });
      });
    }));
  }

  // fromHistory=true → la entrada viene del history (deep-link inicial o popstate atrás/
  // adelante): NO se pushea el param (la URL ya es la correcta). fromHistory=false (drill
  // manual) → pushState para que "atrás" del navegador deshaga el drill.
  function enterFamily(grid, family, auto, fromHistory) {
    const group = familyMap.get(family);
    if (!group || group.members.length < 2) return;
    const { members } = group;

    // recordar la posición de scroll de la colección para restaurarla al volver.
    scrollBeforeDrill = window.scrollY;
    // exponerla para gd-return-position: si el usuario entra a un producto DESDE la
    // familia (vista B), el scroll a guardar para "volver a la colección" es el de la
    // vista A (esta), NO el de la familia (a la que nunca volvemos por el botón).
    document.documentElement.dataset.gdScrollBeforeDrill = String(scrollBeforeDrill);
    // avisar a gd-return-position (pila multi-nivel) el scroll de la COLECCIÓN al entrar a la
    // familia → lo guarda bajo la url madre (sin ?familia). Así, si el usuario baja a un
    // producto desde la familia, "atrás × 2" (PDP → familia → colección) restaura cada nivel:
    // el scroll de familia lo guarda el click/pagehide bajo la url ?familia; el de colección
    // queda acá. Solo en drill MANUAL (fromHistory=false): en popstate/deep-link la vista A ya
    // fue visitada y su scroll ya está en la pila (o no aplica).
    if (!fromHistory) {
      document.dispatchEvent(new CustomEvent('gd:family-enter', {
        detail: { collectionScroll: scrollBeforeDrill },
      }));
    }
    // snapshot del orden VIGENTE del DOM (curado o el que el usuario ordenó) para
    // restaurarlo al salir → no se pierde el ordenamiento elegido.
    orderBeforeDrill = [...grid.querySelectorAll(ITEM_SELECTOR)];

    // 1. ocultar TODOS los items que no son de esta familia
    grid.querySelectorAll(ITEM_SELECTOR).forEach((li) => {
      const own = li.getAttribute('data-family') === family;
      li.classList.toggle(HIDDEN_DRILL, !own);
      if (own) {
        // mostrar todos los hermanos de la familia como productos normales
        li.classList.remove(HIDDEN_SIBLING, HIDDEN_FILTER);
        li.classList.remove('gd-family-rep'); // dentro, el rep es un producto más
      }
    });

    // 2. reordenar los hermanos para que queden consecutivos al INICIO del grid
    //    (el catálogo no los trae contiguos). El <ul> que los contiene es el padre
    //    de los <li>. Movemos el primero al frente y encadenamos el resto tras él.
    const ul = members[0].parentElement;
    let anchor = null;
    members.forEach((li) => {
      if (anchor === null) ul.prepend(li);
      else anchor.after(li);
      anchor = li;
    });

    drilledFamily = family;
    document.documentElement.classList.add('gd-in-family');
    // URL COMPARTIBLE: reflejar la familia abierta en ?familia=<id> sin recargar (igual que
    // gd-pdp.js hace con el swap de versión). Así el usuario puede compartir/recargar la vista
    // B. El param lo IGNORA el server de Shopify (como ?cat= del filtro) → no rompe el motor
    // client-side. setURLParam preserva los otros params (ej. ?cat= si hubiera).
    //
    // pushState en el drill MANUAL (crea entrada de history → "atrás" del navegador vuelve a la
    // colección sin familia, client-side e inmediato vía popstate). PERO cuando entramos desde
    // el history (deep-link inicial o un popstate atrás/adelante) NO tocamos el history: la URL
    // ya es la correcta y pushear duplicaría/descuadraría las entradas. fromHistory lo marca.
    if (!fromHistory) setURLParam('familia', family, 'push');
    markGridColumns(grid); // recalcular rieles/internas para el nuevo set visible
    animateGridEntrance(grid); // los hermanos de la familia emergen (fade + leve subida)

    if (auto) {
      // AUTO-DESPLIEGUE: la colección tiene 1 sola card y es familia → mostramos sus
      // productos directo, SIN cromo de drill (no hay "colección" a la que volver: la
      // colección ES esta familia). Sin breadcrumb, sin botón volver, sin scroll. El
      // conteo lo deja el header (productos reales, ya correcto vía data-total).
      return;
    }
    renderBreadcrumb(grid, group);
    renderDrillFooter(grid, group); // botón "volver" al final de la familia
    updateCount(members.length);
    scrollToCollectionTop(); // entrar a la familia = ver su inicio (breadcrumb + 1ª fila)
  }

  /**
   * Lleva el scroll al INICIO del bloque de colección (breadcrumb / header), no al
   * tope absoluto de la página. Al entrar/salir de una familia cambia el contexto;
   * quedarse scrolleado abajo dejaría al usuario en medio de la nueva vista, sin ver
   * su título ni su primera fila. Se descuenta la altura del navbar fijo. Si ya
   * estamos arriba, no hace nada (evita un salto innecesario).
   */
  function scrollToCollectionTop() {
    const anchor =
      document.querySelector('.gd-drill-breadcrumb') ||
      document.querySelector('.gd-collection-header') ||
      document.querySelector('.product-grid-container');
    if (!anchor) return;
    // navbar fijo de Galpón = .header (position:fixed, ~84px). Descontamos su alto
    // para que el ancla no quede tapada. (Hay un <header> sticky vacío de 0px que NO
    // es el que tapa — por eso apuntamos a .header explícito.)
    const header = document.querySelector('.header');
    const navH = header && getComputedStyle(header).position === 'fixed'
      ? Math.round(header.getBoundingClientRect().height)
      : 0;
    const y = window.scrollY + anchor.getBoundingClientRect().top - navH - 16;
    if (window.scrollY <= y + 8) return; // ya estamos arriba → no saltar
    // INSTANT, no smooth: entrar a una familia es un cambio de CONTEXTO (la grilla se
    // reemplaza). El salto instantáneo se funde con el cambio de contenido → el
    // usuario aparece ya en el inicio de la familia, sin ver ningún deslizamiento
    // (como navegar a otra página). El smooth mostraría un viaje innecesario.
    window.scrollTo({ top: Math.max(0, y), behavior: 'instant' });
  }

  /** Aplica un orden dado (array de <li>) al DOM: re-anexa en ese orden. */
  function applyOrder(order) {
    if (!order || !order.length) return;
    const ul = order[0] && order[0].parentElement;
    if (!ul) return;
    order.forEach((li) => ul.appendChild(li)); // appendChild de nodo existente = mover
  }

  /**
   * AGOTADOS AL FINAL a nivel COLECCIÓN (vista A). Reordena las FAMILIAS del grid poniendo
   * las que tienen stock primero y las totalmente agotadas al final — preservando el orden
   * de Shopify (MOST_RELEVANT) dentro de cada grupo (estable). Cada familia se mueve como
   * BLOQUE (su rep + sus miembros ocultos, consecutivos) para no romper el drill. "Familia
   * disponible" = su rep tiene stock (tras stableSortAvailableFirst, el rep es disponible si
   * algún miembro lo está). Debe llamarse DESPUÉS de collapseFamilies (que llena familyMap y
   * fija reps) y produce el nuevo orden base sobre el que trabajan filtro/drill.
   * Devuelve el array ordenado (para guardarlo como orden vigente).
   */
  function orderSoldOutLast(grid) {
    // recorrer las familias en el orden en que aparece su REP en el DOM actual (= orden
    // Shopify), y partir en dos grupos según disponibilidad del rep. Estable por construcción.
    const seen = new Set();
    const availFamilies = [];
    const soldFamilies = [];
    for (const li of grid.querySelectorAll(ITEM_SELECTOR)) {
      const family = li.getAttribute('data-family') || li.getAttribute('data-product-id');
      if (seen.has(family)) continue;
      seen.add(family);
      const group = familyMap.get(family);
      if (!group) continue;
      (isAvailable(group.rep) ? availFamilies : soldFamilies).push(group);
    }
    // aplanar: cada familia como bloque (rep primero, ya ordenado por stableSortAvailableFirst)
    const order = [];
    for (const group of availFamilies.concat(soldFamilies)) {
      for (const li of group.members) order.push(li);
    }
    applyOrder(order);
    return order;
  }

  /** Sale de la familia (vuelve a vista A): restaura orden, re-colapsa. */
  // Trabajo VISUAL de salir de la familia (vista B → A). fromHistory=true → lo llamó el
  // popstate (el navegador YA cambió la URL): no tocar el history. fromHistory=false → salida
  // directa sin pasar por el history (ej. deep-link directo a ?familia= y luego botón volver,
  // donde no hay entrada de familia que consumir): sincronizar la URL con replaceState.
  function exitFamily(grid, fromHistory) {
    drilledFamily = '';
    document.documentElement.classList.remove('gd-in-family');
    if (!fromHistory) setURLParam('familia', null, 'replace'); // URL limpia (madre)
    clearBreadcrumb();
    clearDrillFooter();
    // restaurar el orden VIGENTE de antes de entrar (curado o el ordenado por el
    // usuario) ANTES de re-colapsar → las cards vuelven a su posición, sin perder el
    // ordenamiento elegido.
    applyOrder(orderBeforeDrill);
    // limpiar el flag de drill y re-aplicar el colapso por familia desde cero
    grid.querySelectorAll(ITEM_SELECTOR).forEach((li) => li.classList.remove(HIDDEN_DRILL));
    collapseFamilies(grid);
    render(grid);
    // restaurar la posición de scroll que tenías al entrar → reaparecés justo en la
    // card de familia que clickeaste, no perdido. (instant: el contenido cambió, un
    // scroll smooth largo se sentiría errático).
    window.scrollTo({ top: scrollBeforeDrill, behavior: 'instant' });
    animateGridEntrance(grid); // la colección reaparece con la misma entrada (simetría A↔B)
  }

  /**
   * "Volver" a la madre desde los botones MANUALES (breadcrumb / footer de la familia).
   * Si entramos a la familia con pushState (drill normal → hay una entrada de history con
   * gdFamily), delega en history.back(): el navegador retrocede y el popstate ejecuta el
   * exit visual → botón y "atrás del navegador" quedan SINCRONIZADOS (misma acción, mismo
   * resultado, sin descuadrar el history). Si NO entramos por push (deep-link directo a
   * ?familia= : no hay entrada previa que consumir), hacemos el exit directo.
   */
  function goBackFromFamily(grid) {
    if (history.state && history.state.gdFamily) history.back();
    else exitFamily(grid, false);
  }

  /** Pinta el breadcrumb "‹ <Colección> › <Familia>" sobre el grid. */
  function renderBreadcrumb(grid, group) {
    clearBreadcrumb();
    const coll = (document.querySelector('.gd-collection-header__title') || {}).textContent || 'Colección';
    const name = familyName(group) || 'Familia';

    const bc = document.createElement('nav');
    bc.className = 'gd-drill-breadcrumb';
    bc.setAttribute('aria-label', 'Ruta de navegación');

    const back = document.createElement('button');
    back.type = 'button';
    back.className = 'gd-drill-back';
    back.innerHTML = '‹ ' + escapeHtml(coll.trim());
    back.addEventListener('click', () => goBackFromFamily(grid));

    const sep = document.createElement('span');
    sep.className = 'gd-drill-sep';
    sep.textContent = '›';

    const current = document.createElement('span');
    current.className = 'gd-drill-current';
    current.textContent = name;

    bc.append(back, sep, current);

    // insertar sobre el contenedor de resultados (antes del grid)
    const anchor = document.querySelector('.product-grid-container') || grid.closest('results-list') || grid.parentElement;
    anchor.parentElement.insertBefore(bc, anchor);
  }

  function clearBreadcrumb() {
    document.querySelectorAll('.gd-drill-breadcrumb').forEach((el) => el.remove());
  }

  /**
   * Botón "volver" al FINAL de la vista de familia: cierra el recorrido sin obligar
   * a subir hasta el breadcrumb. Hace EXACTAMENTE lo mismo que el breadcrumb
   * (exitFamily). Se inserta tras el contenedor del grid.
   */
  function renderDrillFooter(grid, group) {
    clearDrillFooter();
    const coll = (document.querySelector('.gd-collection-header__title') || {}).textContent || 'Colección';

    const wrap = document.createElement('div');
    wrap.className = 'gd-drill-footer';

    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'gd-drill-footer__back';
    btn.innerHTML = '‹ Volver a ' + escapeHtml(coll.trim());
    btn.addEventListener('click', () => goBackFromFamily(grid));

    wrap.appendChild(btn);
    // insertar DENTRO del contenedor del grid, justo después del <ul> de cards →
    // queda pegado al final del bloque (dentro de los rieles), no flotando lejos.
    const ul = grid; // .product-grid
    ul.parentElement.insertBefore(wrap, ul.nextSibling);
  }

  function clearDrillFooter() {
    document.querySelectorAll('.gd-drill-footer').forEach((el) => el.remove());
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  }

  // ---- RENDER + EVENTOS -------------------------------------------------------

  /**
   * Marca qué cards VISIBLES son primera/última columna de su fila, para que el CSS
   * dibuje los rieles de margen ahí y las internas en el resto. NO se puede usar
   * :nth-child en CSS: el colapso por familia oculta cards arbitrarias (display:none)
   * y hay un <script> entre los hijos → el orden del DOM ≠ el orden visual. Acá lo
   * resolvemos por GEOMETRÍA real (posición left de cada card), robusto a colapso,
   * filtrado, drill y a 4/2 columnas. Reaplicar tras cualquier cambio de layout.
   */
  function markGridColumns(grid) {
    const cards = [...grid.querySelectorAll(ITEM_SELECTOR)].filter(
      (li) => li.offsetParent !== null // visible (no display:none)
    );
    // limpiar marcas previas
    cards.forEach((li) => li.classList.remove('gd-col-first', 'gd-col-last', 'gd-row-first', 'gd-row-last'));
    if (!cards.length) return;
    // agrupar por fila según el top redondeado
    const rows = new Map();
    cards.forEach((li) => {
      const top = Math.round(li.getBoundingClientRect().top);
      // tolerancia: agrupar tops cercanos (±4px)
      let key = top;
      for (const k of rows.keys()) { if (Math.abs(k - top) <= 4) { key = k; break; } }
      if (!rows.has(key)) rows.set(key, []);
      rows.get(key).push(li);
    });
    // en cada fila, la card más a la izquierda = primera col; la más a la derecha = última
    for (const rowCards of rows.values()) {
      rowCards.sort((a, b) => a.getBoundingClientRect().left - b.getBoundingClientRect().left);
      rowCards[0].classList.add('gd-col-first');
      rowCards[rowCards.length - 1].classList.add('gd-col-last');
    }
    // PRIMERA FILA (menor top) → lleva la línea horizontal superior que ABRE el bloque de cards
    // (larga, de riel a riel). ÚLTIMA FILA (mayor top) → la línea que CIERRA abajo (larga, igual
    // que la superior) y sus verticales NO se extienden al vacío. Consistencia con la PDP/familia:
    // el bloque se abre arriba y se cierra abajo con líneas largas, no cortas por-card.
    const firstTop = Math.min(...rows.keys());
    const lastTop = Math.max(...rows.keys());
    rows.get(firstTop).forEach((li) => li.classList.add('gd-row-first'));
    rows.get(lastTop).forEach((li) => li.classList.add('gd-row-last'));
  }

  function render(grid) {
    applyFilter(grid);
    syncPills();
    markGridColumns(grid);
  }

  function onPillClick(event, grid) {
    const pill = event.target.closest(PILL_SELECTOR);
    if (!pill) return;
    event.preventDefault();
    const type = pill.getAttribute('data-type') || '';
    activeType = type !== '' && type === activeType ? '' : type;
    saveActiveType(activeType); // recordar el filtro para "volver" desde la PDP
    // filtrar implica volver a vista A. Exit VISUAL directo (síncrono, así el render del filtro
    // que sigue corre ya en vista A) con replaceState: reescribe la entrada actual ?familia= a
    // la URL limpia de la madre → no queda una entrada de history de familia colgada. NO usamos
    // goBackFromFamily aquí: su history.back() dispararía popstate async y el render() de abajo
    // correría antes del exit → desorden.
    if (drilledFamily) exitFamily(grid, false);
    render(grid);
  }

  /**
   * Click en una card. En vista A: si es entrada de familia (rep N>1) → entra (no PDP).
   * Standalone o cualquier card en vista B → click pasa normal a su <a> → PDP.
   */
  function onCardClick(event, grid) {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
    if (drilledFamily) return; // dentro de una familia, los clicks van a la PDP normal

    const li = event.target.closest(ITEM_SELECTOR);
    if (!li) return;
    const family = li.getAttribute('data-family-key'); // solo en entradas de familia N>1
    if (li.classList.contains('gd-family-rep') && family) {
      event.preventDefault();
      enterFamilyWhenReady(grid, family, false, false);
    }
  }


  function init() {
    const grid = document.querySelector(GRID_SELECTOR);
    if (!grid) return;

    // ¿Estás VOLVIENDO a esta colección desde una PDP? gd-return-position marca
    // 'gd:return-position:pending' al click del botón "volver". Se usa para (a) restaurar
    // el filtro de pill y (b) mantener el grid oculto hasta posicionar el scroll (anti-salto).
    // "returning" = volvés a esta colección desde una PDP → restaurar el filtro de pill (y el
    // scroll). Dos señales, con OR (cubren botón Y atrás del navegador):
    //   (a) 'gd:nav-restore-pending' → lo setea gd-return-position.js al click del botón "volver"
    //       de la PDP. (Antes era 'gd:return-position:pending', del return-position viejo; al
    //       reescribirlo a la PILA cambió la key → leer la nueva o `returning` quedaba false y el
    //       filtro NO se restauraba. Esto era el bug que F reportó.)
    //   (b) document.referrer es una PDP (/products/) → cubre el ATRÁS DEL NAVEGADOR, que NO
    //       setea el pending (solo el botón lo hace). Volver de una PDP a la colección, por la vía
    //       que sea, restaura el filtro. Entrar fresco desde el menú/home/directo → referrer no es
    //       PDP → arranca en "Todo".
    let returning = false;
    try {
      const pending = sessionStorage.getItem('gd:nav-restore-pending') === '1';
      let fromPdp = false;
      try { fromPdp = /\/products\//.test(new URL(document.referrer).pathname); } catch {}
      returning = pending || fromPdp;
    } catch {}

    // try/finally: pase lo que pase, el grid SE REVELA (estaba oculto por CSS para
    // evitar el flash). Si el colapso truena, mejor mostrar todo sin colapsar que
    // dejar el grid invisible para siempre.
    try {
      collapseFamilies(grid);
      // AGOTADOS AL FINAL (vista A): familias con stock primero, agotadas al final,
      // respetando el orden de Shopify dentro de cada grupo. Tras collapseFamilies (reps
      // fijados) y antes de render. Deja el DOM en el orden vigente para filtro/drill.
      orderSoldOutLast(grid);
      // Restaurar el filtro de pill SOLO al volver. En entrada fresca NO → ves "Todo".
      // Aplicarlo ANTES del primer render() es clave: así la altura del grid ya es la
      // filtrada cuando se dispara 'gd:grid-ready' y el scroll-restore cae en la card correcta.
      if (returning) {
        const saved = loadActiveType();
        if (saved) activeType = saved;
      } else {
        // entrada fresca a la colección → arrancar en "Todo" y olvidar el filtro viejo,
        // así no se arrastra entre navegaciones distintas (entrar de nuevo = empezar limpio).
        saveActiveType('');
      }
      render(grid);
      // AUTO-DESPLIEGUE: si la colección colapsa a UNA sola card y esa card es una
      // familia N>1 (ej. "Mesas de Centro" = solo "Mesa de Centro Redonda" ×3),
      // mostramos sus productos directo — la card sola obligaría a un click vacío
      // (la colección ES esa familia). NO aplica si hay 2+ cards (1 familia + 1
      // individual sigue siendo grilla normal) ni si la única card es individual.
      if (familyMap.size === 1) {
        const [[soleFamily, group]] = familyMap;
        // auto-despliegue en carga inicial → fromHistory=true (no pushea: la URL ya es la de
        // esta vista y no hay madre a la que "volver" con atrás).
        if (group.members.length > 1) enterFamilyWhenReady(grid, soleFamily, true, true);
      } else if (!returning) {
        // DEEP-LINK de familia: si la URL trae ?familia=<id> (link compartido / recarga),
        // abrir esa familia directo (vista B). Va ANTES del 'gd:grid-ready' del finally →
        // la familia queda montada ANTES de que el grid se revele (el CSS lo oculta hasta
        // .gd-grid-ready): el usuario nunca ve "madre → salta a familia". Solo en carga FRESCA: si
        // estás volviendo (pending), manda el scroll-restore (vuelve a vista A, no a la
        // familia — "volver" nunca va a vista B). enterFamily valida que el id exista y sea
        // N>1 → si la familia ya no está (link viejo), no abre nada y quedás en la madre.
        try {
          const fam = new URLSearchParams(location.search).get('familia');
          // auto=FALSE: el deep-link entra a la familia como un drill NORMAL, CON breadcrumb
          // y botón "volver" → el visitante que llega por el link puede salir a la madre. (No
          // confundir con auto=true del caso "colección = 1 sola familia", que suprime el cromo
          // porque ahí no hay madre a la que volver.)
          // fromHistory=true: la URL YA trae ?familia= (deep-link/recarga) → entrar SIN pushear
          // (pushear duplicaría la entrada actual del history).
          if (fam && familyMap.has(fam)) enterFamilyWhenReady(grid, fam, false, true);
        } catch {}
      }
      document.addEventListener('click', (e) => onPillClick(e, grid));
      grid.addEventListener('click', (e) => onCardClick(e, grid), true);

      // POPSTATE — atrás/adelante del navegador dentro de la colección. El drill de familia usa
      // pushState (?familia=<id>), así que "atrás" navega same-document (NO recarga): sincronizamos
      // la VISTA con la URL, todo client-side e inmediato (igual de rápido que entrar a la familia).
      //   URL con ?familia=X y no estamos en X → entrar a X (fromHistory: no re-pushear).
      //   URL sin ?familia y estamos en una      → salir a la madre (fromHistory: el browser ya
      //                                             cambió la URL, no la tocamos).
      // Cubre AMBOS: atrás desde familia → madre, y adelante desde madre → familia. El botón
      // "volver" manual delega en history.back() (goBackFromFamily) → cae también acá, un solo camino.
      window.addEventListener('popstate', () => {
        const fam = new URLSearchParams(location.search).get('familia');
        if (fam && familyMap.has(fam)) {
          if (drilledFamily !== fam) enterFamilyWhenReady(grid, fam, false, true);
        } else if (drilledFamily) {
          exitFamily(grid, true);
        }
      });

      // re-marcar columnas al cambiar el layout (4↔2 col). Debounce simple por rAF.
      let raf = 0;
      window.addEventListener('resize', () => {
        if (raf) return;
        raf = requestAnimationFrame(() => { raf = 0; markGridColumns(grid); });
      });
    } finally {
      grid.classList.add('gd-grid-ready');
      // ANTI-SALTO al volver: si estás VOLVIENDO (pending), NO revelamos el grid todavía.
      // El grid tiene su altura definitiva (.gd-grid-ready) pero sigue oculto por
      // .gd-grid-restoring → gd-return-position hace el scrollTo a la posición exacta y
      // RECIÉN AHÍ lo descorre. Así el usuario no ve "caigo arriba → salto": aparece
      // directo en su posición. En entrada fresca el grid se revela normal (sin la clase).
      // Red de seguridad: si return-position no descorre (no corre / falla), un timeout
      // lo revela igual — nunca dejar el grid invisible para siempre.
      if (returning) {
        // v13 (sin velo): ocultar TODO el main de colección hasta posicionar (el CSS
        // hace el fade al quitarse la clase; failsafe CSS puro 2.2s + este de 4s).
        document.documentElement.classList.add('gd-returning');
        setTimeout(() => document.documentElement.classList.remove('gd-returning'), 4000);
        grid.classList.add('gd-grid-restoring');
        // 4000 (era 1500): el restore ahora espera la hidratación (~1s en móvil) antes de
        // posicionar — revelar a los 1500 mostraba la página arriba y luego el salto.
        // Sigue siendo un failsafe duro: pase lo que pase, el grid se revela.
        setTimeout(() => grid.classList.remove('gd-grid-restoring'), 4000);
      }
      // tras revelar, el layout es definitivo → marcar columnas con geometría real.
      markGridColumns(grid);
      // Señal para otros sistemas (gd-return-position): el grid ya colapsó familias y
      // tiene su altura DEFINITIVA. Restaurar scroll ANTES de esto fallaba (la altura
      // cambiaba bajo los pies → scroll clampeado/erróneo, la intermitencia).
      // HIDRATACIÓN en background (D-coleccion-1): arrancarla ANTES de despachar
      // gd:grid-ready — los listeners de esa señal (scroll-restore) consultan
      // window.gdGridHydration() SINCRÓNICAMENTE al recibirla; si la promesa aún no
      // existe, restaurarían con la altura de página-1 (carrera medida: delta ~230px).
      // El fetch es network-bound: dispararlo acá no compite con el render ya hecho.
      hydrate(grid);
      document.dispatchEvent(new CustomEvent('gd:grid-ready'));
    }
  }

  // ── ARRANQUE ROBUSTO (fix jul 2026, sesión "frontend post deploy") ───────────
  // BUG que arregla (reproducido en WebKit/Safari real con webkit2gtk): el arranque
  // viejo era `if (readyState!=='loading') init(); else addEventListener('DOMContentLoaded', init)`.
  // Eso tiene DOS carreras que en WebKit cuelgan la colección (velo pegado, "página clara
  // sin items"), de forma NO determinista (por eso a unos clientes sí y a otros no):
  //
  //   1) Este script es <script defer>. En WebKit, con HTML grande (64 productos = mucho DOM),
  //      el defer puede ejecutarse mientras el parser AÚN no cerró → readyState==='loading' →
  //      cae en el else y registra el listener de DOMContentLoaded. Pero en esa ventana el
  //      evento puede dispararse en un microtask que WebKit ordena distinto que Chrome → el
  //      listener llega tarde y NUNCA corre init(). (Medido: readyState quedaba en 'loading'
  //      con solo 23-45 de 64 items parseados.)
  //   2) init() hace querySelector('.product-grid') y si no está, return silencioso SIN
  //      reintentar. El grid vive dentro de <results-list> (custom element, type=module):
  //      si el upgrade del componente o el parseo del DOM va atrasado, el grid no existe
  //      todavía → init() aborta para siempre.
  //
  // Sin init() → no colapsa familias → no emite 'gd:grid-ready' → el CSS que oculta el grid
  // hasta esa clase (galpon-overrides.css) lo dejaba invisible → colección VACÍA. Ese CSS ya
  // tiene su propia red de seguridad (animación `gd-grid-failsafe`, 1.5 s), pero igual
  // queremos que init() corra: si no, no hay familias, solo cards sueltas.
  //
  // Fix: arrancar de forma IDEMPOTENTE (init ya es re-entrante-seguro: si el grid no está,
  // reintenta; si ya colapsó, no re-colapsa porque decorateRepresentative es idempotente y
  // familyMap se limpia cada vez), esperando a que el .product-grid EXISTA DE VERDAD —no a un
  // evento que se puede perder— con múltiples disparadores que se cubren entre sí. El primero
  // que encuentre el grid gana; los demás quedan neutralizados por el flag `started`.
  let started = false;
  function tryStart() {
    if (started) return true;
    const grid = document.querySelector(GRID_SELECTOR);
    if (!grid) return false;      // grid aún no en el DOM → que reintente otro disparador
    started = true;
    init();
    return true;
  }

  // Disparador A: polling por rAF acotado. Cubre el caso del custom element / DOM que monta
  // tarde SIN depender de ningún evento. Se detiene apenas arranca o tras ~5 s (red de
  // seguridad dura: nunca quedar reintentando para siempre).
  let rafTries = 0;
  const MAX_RAF_TRIES = 300; // ~5 s a 60 fps
  (function pollForGrid() {
    if (tryStart()) return;
    if (rafTries++ > MAX_RAF_TRIES) return;
    requestAnimationFrame(pollForGrid);
  })();

  // Disparador B y C: eventos del ciclo de vida del documento, por si el grid aparece
  // exactamente en ese momento. Redundantes con el polling a propósito (cinturón + tirantes):
  // en WebKit un evento puede perderse, pero no los tres caminos a la vez.
  document.addEventListener('DOMContentLoaded', tryStart);
  window.addEventListener('load', tryStart);
})();
